import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from '../hooks/use-toast';

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: ''
  });

  const { login, register } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast({
          title: language === 'en' ? 'Welcome back!' : '¡Bienvenida de vuelta!',
          description: language === 'en' ? 'Successfully logged in' : 'Sesión iniciada correctamente'
        });
      } else {
        await register(formData.email, formData.password, formData.firstName, formData.lastName);
        toast({
          title: language === 'en' ? 'Welcome to The Wallness!' : '¡Bienvenida a The Wallness!',
          description: language === 'en' ? 'Your account has been created' : 'Tu cuenta ha sido creada'
        });
      }
      navigate('/dashboard');
    } catch (error) {
      toast({
        title: language === 'en' ? 'Error' : 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0d1f1f] via-[#1a3334] to-[#0d1f1f] flex items-center justify-center px-4">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center space-x-2 mb-8">
          <div className="text-3xl font-bold text-white tracking-tight">
            <span className="text-white">THE</span>
            <span className="text-emerald-400 ml-1">WALLNESS</span>
          </div>
          <div className="h-8 w-1 bg-emerald-400"></div>
        </Link>

        {/* Card */}
        <div className="bg-gray-900/50 backdrop-blur-xl rounded-3xl p-8 border border-gray-700/50 shadow-2xl">
          {/* Toggle */}
          <div className="flex bg-gray-800/50 rounded-full p-1 mb-8">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-3 rounded-full text-sm font-medium transition-all ${
                isLogin 
                  ? 'bg-emerald-500 text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {language === 'en' ? 'Login' : 'Iniciar Sesión'}
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-3 rounded-full text-sm font-medium transition-all ${
                !isLogin 
                  ? 'bg-emerald-500 text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {language === 'en' ? 'Register' : 'Registrarse'}
            </button>
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white mb-2">
              {isLogin 
                ? (language === 'en' ? 'Welcome Back' : 'Bienvenida de Vuelta')
                : (language === 'en' ? 'Start Building Your Wall' : 'Comienza a Construir Tu Muro')
              }
            </h1>
            <p className="text-gray-400">
              {isLogin
                ? (language === 'en' ? 'Continue your wellness journey' : 'Continúa tu camino de bienestar')
                : (language === 'en' ? 'Create your account today' : 'Crea tu cuenta hoy')
              }
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div className="grid grid-cols-2 gap-4">
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    type="text"
                    placeholder={language === 'en' ? 'First name' : 'Nombre'}
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    className="pl-10 bg-gray-800/50 border-gray-700 text-white placeholder:text-gray-500 h-12 rounded-xl"
                    required={!isLogin}
                  />
                </div>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    type="text"
                    placeholder={language === 'en' ? 'Last name' : 'Apellido'}
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    className="pl-10 bg-gray-800/50 border-gray-700 text-white placeholder:text-gray-500 h-12 rounded-xl"
                    required={!isLogin}
                  />
                </div>
              </div>
            )}

            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <Input
                type="email"
                placeholder={language === 'en' ? 'Email' : 'Correo electrónico'}
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="pl-10 bg-gray-800/50 border-gray-700 text-white placeholder:text-gray-500 h-12 rounded-xl"
                required
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder={language === 'en' ? 'Password' : 'Contraseña'}
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="pl-10 pr-10 bg-gray-800/50 border-gray-700 text-white placeholder:text-gray-500 h-12 rounded-xl"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl mt-6 group"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  {isLogin 
                    ? (language === 'en' ? 'Login' : 'Iniciar Sesión')
                    : (language === 'en' ? 'Create Account' : 'Crear Cuenta')
                  }
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </Button>
          </form>

          {/* Back to home */}
          <div className="mt-6 text-center">
            <Link to="/" className="text-gray-400 hover:text-emerald-400 text-sm transition-colors">
              {language === 'en' ? '← Back to home' : '← Volver al inicio'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
